import {useMutation, useQueryClient, useQuery} from '@github-ui/react-query'
import {
  COPILOT_SPACES_QUERY_KEY,
  COPILOT_SPACES_COUNT_QUERY_KEY,
  copilotSpacesCategoryQueryKey,
  copilotSpacesQueryKey,
  deleteCopilotSpace,
  fetchCopilotSpace,
  fetchCopilotSpaces,
  fetchMenuActions,
  fetchSpacesCount,
  fetchVisibilitySettings,
} from '../utils/fetch'
import type {
  CopilotSpaceCategory,
  CopilotSpaceId,
  CopilotSpace,
  CopilotSpacePayload,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useEffect, useMemo} from 'react'
import {useChatStateValue} from '@github-ui/copilot-chat/CopilotChatContext'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {getCopilotSpacePath} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export class CopilotSpaceNotFoundError extends Error {
  constructor(message = 'Copilot Space not found') {
    super(message)
    this.name = 'CopilotSpaceNotFoundError'
  }
}

export class CopilotSpaceSSOError extends Error {
  protectedOrganizations: string[]
  constructor(protectedOrganizations: string[], message = 'Copilot Space SSO error') {
    super(message)
    this.protectedOrganizations = protectedOrganizations
    this.name = 'CopilotSpaceSSOError'
  }
}

export function useDeleteCopilotSpace() {
  const queryClient = useQueryClient()
  const manager = useChatManager()
  return useMutation({
    mutationFn: async (item: CopilotSpaceId) => {
      const result = await deleteCopilotSpace(item)
      // Ensure result is not an error type before returning
      if (!result.ok) {
        throw new Error(result.error)
      }

      // remove the deleted space from the chat manager state
      manager.dispatch({type: 'REMOVE_CUSTOM_COPILOT', customCopilotId: item})
      // Only return the payload or a safe object
      return {status: result.status, ok: true, payload: result.payload}
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
    },
  })
}

/**
 * A hook to manage the custom copilots in the chat manager.
 * Run only for side effects. Eventually we'll replace this with the underlying
 * `useManagedFetchCopilotSpaces` hook.
 * It fetches the custom copilots by category (starred, user_owned, org_owned, shared_with_user)
 * and updates the chat manager state for each category independently.
 * @param enabled - Whether to enable the fetching of custom copilots.
 */
export function useManagedFetchCopilotSpaces(enabled: boolean) {
  const manager = useChatManager()
  const splitIndexCallsEnabled = isFeatureEnabled('copilot_spaces_split_index_calls')

  // Legacy: Fetch all spaces in a single call (when feature flag is disabled)
  const legacyResult = useFetchCopilotSpaces(enabled && !splitIndexCallsEnabled, {throwOnError: false})

  // Legacy behavior: dispatch SET_CUSTOM_COPILOTS when feature flag is disabled
  useEffect(() => {
    if (!splitIndexCallsEnabled && legacyResult.data) {
      manager.dispatch({type: 'SET_CUSTOM_COPILOTS', customCopilots: legacyResult.data})
    }
  }, [legacyResult.data, manager, splitIndexCallsEnabled])
}

/**
 * Hook for fetching custom copilots by category from the server.
 * @param category - The category of spaces to fetch (starred, user_owned, org_owned, shared_with_user)
 * @param enabled - Whether to enable the fetching
 * @param options - Additional query options
 */
export function useFetchCopilotSpacesByCategory(
  category: CopilotSpaceCategory,
  enabled: boolean,
  options: {throwOnError?: boolean} = {},
) {
  return useQuery({
    queryKey: copilotSpacesCategoryQueryKey(category),
    queryFn: () => fetchCopilotSpaces(category),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError: options.throwOnError,
  })
}

/**
 * The default hook for fetching all custom copilots from the server (without category filter).
 * @deprecated This function fetches all spaces in a single call, which doesn't support independent tab loading.
 * Use useFetchCopilotSpacesByCategory for fetching specific categories, or useManagedFetchCopilotSpaces
 * for automatic fetching of all categories with independent loading. This function may be removed in a future release.
 */
export function useFetchCopilotSpaces(enabled: boolean, options: {throwOnError?: boolean} = {}) {
  return useQuery({
    queryKey: COPILOT_SPACES_QUERY_KEY,
    queryFn: () => fetchCopilotSpaces(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    throwOnError: options.throwOnError,
  })
}

function hasCopilotSpaceData(payload: CopilotSpacePayload): payload is CopilotSpace {
  return (payload as CopilotSpace).id !== undefined
}

export function useFetchCopilotSpace(customCopilotId: CopilotSpaceId | null) {
  return useQuery({
    queryKey: copilotSpacesQueryKey(customCopilotId),
    queryFn: async () => {
      const response = await fetchCopilotSpace(customCopilotId)
      if (!response.ok) {
        if (response.status === 404) {
          throw new CopilotSpaceNotFoundError()
        }
        throw new Error(response.error)
      }
      if (!hasCopilotSpaceData(response.payload)) {
        throw new CopilotSpaceSSOError(response.payload.protectedOrganizations)
      }
      return response.payload
    },
    enabled: !!customCopilotId,
  })
}

// We split up the customCopilotId because the linter wants each parameter used in the query key,
// but directly passing the whole object in seems to key on object identity and lead to lots of
// redundant API calls.
export function useFetchVisibilitySettings(copilotSpaceOwner: string, copilotSpaceNumber: number) {
  return useQuery({
    queryKey: ['visibility-settings', copilotSpaceOwner, copilotSpaceNumber],
    queryFn: async () => {
      return fetchVisibilitySettings({owner: copilotSpaceOwner, id: copilotSpaceNumber})
    },
    enabled: copilotSpaceOwner !== '' && copilotSpaceNumber !== -1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useFetchMenuActions(copilotSpaceOwner: string, copilotSpaceNumber: number) {
  return useQuery({
    queryKey: ['menu-actions', copilotSpaceOwner, copilotSpaceNumber],
    queryFn: async () => {
      return fetchMenuActions({owner: copilotSpaceOwner, id: copilotSpaceNumber})
    },
    enabled: copilotSpaceOwner !== '' && copilotSpaceNumber !== -1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching the count of spaces for each category.
 * @param enabled - Whether to enable the fetching
 * @returns Query result with the spaces counts
 */
export function useFetchSpacesCount(enabled: boolean) {
  return useQuery({
    queryKey: COPILOT_SPACES_COUNT_QUERY_KEY,
    queryFn: fetchSpacesCount,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for fetching all categorized spaces with their loading states.
 * This hook fetches spaces by category and returns both the categorized data
 * and the loading state for each category.
 * @param enabled - Whether to enable the fetching of categorized spaces
 * @returns Object containing categorizedSpaces and categorizedLoading
 */
export function useFetchCategorizedSpaces(enabled: boolean) {
  const {data: starredCopilotSpaces, isLoading: isStarredCopilotSpacesLoading} = useFetchCopilotSpacesByCategory(
    'starred',
    enabled,
  )
  const {data: userOwnedCopilotSpaces, isLoading: isUserOwnedCopilotSpacesLoading} = useFetchCopilotSpacesByCategory(
    'user_owned',
    enabled,
  )
  const {data: orgOwnedCopilotSpaces, isLoading: isOrgOwnedCopilotSpacesLoading} = useFetchCopilotSpacesByCategory(
    'org_owned',
    enabled,
  )
  const {data: sharedWithUserCopilotSpaces, isLoading: isSharedWithUserCopilotSpacesLoading} =
    useFetchCopilotSpacesByCategory('shared_with_user', enabled)

  const categorizedSpaces = useMemo(
    () => ({
      starred: starredCopilotSpaces,
      userOwned: userOwnedCopilotSpaces,
      orgOwned: orgOwnedCopilotSpaces,
      sharedWithUser: sharedWithUserCopilotSpaces,
    }),
    [starredCopilotSpaces, userOwnedCopilotSpaces, orgOwnedCopilotSpaces, sharedWithUserCopilotSpaces],
  )

  const categorizedLoading = useMemo(
    () => ({
      starred: isStarredCopilotSpacesLoading,
      userOwned: isUserOwnedCopilotSpacesLoading,
      orgOwned: isOrgOwnedCopilotSpacesLoading,
      sharedWithUser: isSharedWithUserCopilotSpacesLoading,
    }),
    [
      isStarredCopilotSpacesLoading,
      isUserOwnedCopilotSpacesLoading,
      isOrgOwnedCopilotSpacesLoading,
      isSharedWithUserCopilotSpacesLoading,
    ],
  )

  return {categorizedSpaces, categorizedLoading}
}

/**
 * Custom copilots are enabled for:
 * 1. users with the :copilot_custom_copilots feature flag, which some will be individually opted into
 * 2. The following types of users with the :copilot_custom_copilots_feature_preview feature flag:
 *    a. users with Copilot Individual (CI) licenses, for whom preview features are always enabled
 *    b. CB and CE users whose orgs have opted into preview features
 *    c. Copilot Free users, signified by `has_limited_access` on their plan
 * This values is serialized down from the copilot_chat_helper and put into the chat state.
 */
export function useCustomCopilotsEnabled() {
  return useChatStateValue('customCopilotsEnabled')
}

/**
 * Hook that returns the current number of Copilot spaces.
 *
 * @returns The total count of existing Copilot spaces
 */
export function useNumberOfSpaces(): number {
  const copilotSpaces = useChatStateValue('customCopilots')
  const copilotSpacesCount = copilotSpaces?.length ?? 0
  return copilotSpacesCount
}

/* Generates a default name based on the current number of existing spaces.
 * The default name follows the pattern "New Space {count + 1}" where count is the number of existing spaces.
 *
 * @returns the default name as a string
 */
export function useAutoSpaceName(): string {
  const copilotSpacesCount = useNumberOfSpaces()
  const defaultName = useMemo(() => `New Space ${copilotSpacesCount + 1}`, [copilotSpacesCount])
  return defaultName
}

/** Hook to generate a report URL to Github support for a Copilot Space.
 * @param selectedCopilotSpaceId - The ID of the selected Copilot Space, or null if none is selected.
 * @returns The constructed report URL or undefined if not applicable.
 */
export function useReportUrl(selectedCopilotSpaceId: CopilotSpaceId | null): string | undefined {
  const reportUrl = useMemo(() => {
    if (!selectedCopilotSpaceId) return undefined
    const {owner} = selectedCopilotSpaceId
    const host = ssrSafeWindow?.location.origin
    if (!host) return undefined

    const spaceUrl = getCopilotSpacePath(selectedCopilotSpaceId)
    const fullUrl = `${host}${spaceUrl}`
    const query = `content_url=${encodeURIComponent(fullUrl)}&report=${owner}+(user)`
    const reportTargetUrl = `${host}/contact/report-content?${query}`

    return reportTargetUrl
  }, [selectedCopilotSpaceId])

  return reportUrl
}
